import { Injectable } from "@angular/core";
import { ScheduledShift, Shift, ShiftRole } from "ng/src/app/shifts/shift.model";
import { HttpClient } from "@angular/common/http";
import { LogService } from "ng/src/app/logs/log.service";
import { HALResponse } from "ng/src/app/shared/model/app.model";
import { GeneralParameters } from "app/shared/model/request-parameter.model";
import { BehaviorSubject, EMPTY, Observable, Subscription, timer } from "rxjs";
import { LogRoleId } from "ng/src/app/logs/log.model";
import { exhaustMap, map, tap } from "rxjs/operators";
import { PagedEntity } from "app/shared/model/app.model";
import { Utils } from "ng/src/app/core/utils";
import { AppStateService } from "../core/services/app-state.service";

@Injectable({
	providedIn: 'root'
})
export class ShiftsService {
	
	constructor(
		private http: HttpClient
	) {
		LogService.selectedLog$.pipe(
			tap(() => {
				this._activeShifts$.next([]);
				this._onShift$.next(false);
				this._userShift$.next(undefined)
			}),
			exhaustMap((log) => log ? this.refreshActiveShifts() : EMPTY)
		)
		.subscribe();
		
		this.userShift$.subscribe((shift) => {
			this.shiftRefreshSubscription?.unsubscribe();
			if (!shift || !shift.millisRemaining || !Utils.isSafeNumber(shift.millisRemaining))
				return;

			this.shiftRefreshSubscription = timer(shift.millisRemaining + 1000)
				.subscribe(() => {
					this.refreshActiveShifts().subscribe();
				})
		})
	}
	
	private shiftRefreshSubscription?: Subscription;
	
	private _activeShifts$ = new BehaviorSubject<Shift[]>([]);
	private _onShift$ = new BehaviorSubject<boolean>(false);
	private _userShift$ = new BehaviorSubject<Shift | undefined>(undefined);
	
	readonly activeShifts$ = this._activeShifts$.asObservable();
	readonly onShift$ = this._onShift$.asObservable();
	readonly userShift$ = this._userShift$.asObservable();
	
	private get currentUser() {
		return LogService.selectedLog?.activeLogUser;
	}
	
	private get selectedLog() {
		return LogService.selectedLog;
	}
	
	get activeShifts() {
		return this._activeShifts$.getValue();
	}
	
	get isOnShift() {
		return this._onShift$.getValue();
	}
	
	get userShift() {
		return this._userShift$.getValue();
	}
	
	
	refreshActiveShifts() {
		return this.fetchActiveShifts().pipe(
			tap(shifts => {
				// Somehow, shift was undefined inside shifts.find() when Sandy logged into test and was prompted
				// to refresh LogBook via the big red bar. This caused a red error popup. Not sure how it happened,
				// but the optional chain on shift should prevent the error in this scenario. 
				const userShift = shifts.find(shift => shift?.user.id === this.currentUser?.user?.id);
				
				if (this.selectedLog && this.selectedLog.userRole.id === LogRoleId.WRITE_ONSHIFT) {
					this.selectedLog.isWriteOnShiftRole = true;
					this.selectedLog.role = (!!userShift) ? 'WRITE' : 'READ';
				}
				
				this._onShift$.next(!!userShift);
				this._userShift$.next(userShift);
				this._activeShifts$.next(shifts);
			})
		);
	}
	
	extendShift(shift: Shift) {
		const dt = new Date(shift.endTime.replace(/\+0000/, 'Z'));
		const newEnd = new Date(dt.getTime() + 15*60*1000);
		const body = {
			endTime: newEnd.toISOString()
		}
		
		return this.http.patch(shift._links.self.href, body);
	}
	
	signOn(details: {startTime: Date, endTime: Date, role: ShiftRole | undefined}): Observable<Shift> {
		const body = {
			beginTime: details.startTime.toISOString(),
			endTime: details.endTime.toISOString(),
			log: this.selectedLog?._links?.self?.href,
			role: details.role?._links?.self?.href ?? null,
			userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
		}
		return this.http.post<Shift>('/api/shifts', body);
	}
	
	signOut(shift: Shift) {
		const body = {
			signOut: true
		}
		
		return this.http.patch(shift._links.self.href, body);
	}
	
	getShiftRoles() {
		const params: GeneralParameters = {
			log: LogService.selectedLog?.id
		}
		
		return this.http.get<HALResponse<{ shiftRoles: ShiftRole[] }>>('/api/shiftRoles', { params: params })
			.pipe(map(res => PagedEntity.from(res._embedded.shiftRoles, res.page)));
	}
	
	getScheduledShifts() {
		const params: GeneralParameters = {
			log: LogService.selectedLog?.id
		}
		
		return this.http.get<HALResponse<{ scheduledShifts: ScheduledShift[] }>>('/api/scheduledShifts', {
			params: params
		})
			.pipe(map(res => PagedEntity.from(res._embedded.scheduledShifts, res.page)));
	}
	
	findBestShift(shifts: ScheduledShift[]): ScheduledShift | undefined {
		const varShifts = shifts.map(s => Object.assign({} as ScheduledShift, s));
		const bestStartDates = varShifts.map(s => {
			return {
				shift: s,
				date: this.createBestStartDate(s)
			}
		});
		const sorted = bestStartDates.sort(Utils.createSortFunction(s => this.compareDateToNow(s.date)));
		
		return sorted[0]?.shift;
	}
	
	getShiftDates(shift: ScheduledShift): [Date, Date] {
		const start = new Date();
		const end = new Date();
		
		const adjust = (date: Date, shiftTime: string | undefined) => {
			date.setSeconds(0);
			date.setMilliseconds(0);
			
			if (!shiftTime) return;
			const sTime = parseInt(shiftTime);
			const hour = Math.floor(sTime / 100);
			const min = sTime % 100;
			date.setHours(hour, min);
		};
		
		adjust(start, shift?.beginTime);
		adjust(end, shift?.endTime);
		
		if (start > end) {
			end.setTime(end.getTime() + (24*60*60*1000));
		}
		return [start, end];
	}

	fetchActiveUserShifts() {
		const params: GeneralParameters = {
			user: AppStateService.currentUser?.id,
			active: true
		};

		return this.http.get<HALResponse<{ shifts: Shift[] }>>('/api/shifts', { params })
			.pipe(map(res => PagedEntity.from(res._embedded.shifts, res.page)));
	}
	
	private fetchActiveShifts() {
		const params: GeneralParameters = {
			log: LogService.selectedLog?.id,
			active: true
		}
		
		return this.http.get<HALResponse<{ shifts: Shift[] }>>('/api/shifts', {
			params: params
		})
			.pipe(map((res) => PagedEntity.from(res._embedded.shifts, res.page)));
	}
	
	private compareDateToNow (dt: Date = new Date()) {
		const now = new Date();
		return Math.abs(now.getTime() - dt.getTime());
	}
	
	private createBestStartDate (shift: ScheduledShift) {
		const dates = shift.weekdays.map(weekday => {
				let date = new Date();
				let shiftTime = shift.beginTime;
				
				// This includes a correction for java/javascript day numbers
				const distance = (weekday + 6 - date.getDay()) % 7;
				date.setDate(date.getDate() + distance);
				
				date.setSeconds(0);
				date.setMilliseconds(0);
				
				const sTime = parseInt(shiftTime);
				const hour = Math.floor(sTime / 100);
				const min = sTime % 100;
				date.setHours(hour, min);
				return date;
			})
			.sort(Utils.createSortFunction(dt => this.compareDateToNow(dt)));
		
		return dates[0];
	};
	
}